import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { Textarea, TextField } from '@/shared/components/TextField';
import CreateLayout from '@/shared/layout/createLayout';
import { useMemo } from 'react';
import {
  useCreateSubscription,
  PERIOD_TYPE_OPTIONS,
  PERIOD_OPTIONS_MONTHLY,
  PERIOD_OPTIONS_YEARLY,
  MAX_PRICE,
  type CreateSubscriptionFormProps,
} from '@/epics/subscriptionManagment/hooks/useCreateSubscription';

const CreateSubscription = () => {
  const {
    formData,
    setFormData,
    durationType,
    setDurationType,
    period,
    setPeriod,
    cycle,
    setCycle,
    cycleOptions,
    errors,
    setErrors,
    isPreviewStep,
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    formatPriceWithSpaces,
    clampPriceToMax,
    getBillingDescription,
  } = useCreateSubscription();

  const CreateSubscriptionHeader = () => {
    return <p className="text-titleLarge text-neutral-10 ">Utwórz plan</p>;
  };

  const CreateSubscriptionForm = useMemo(
    () =>
      function CreateSubscriptionForm({
        formData,
        setFormData,
        durationType,
        setDurationType,
        period,
        setPeriod,
        cycle,
        setCycle,
        cycleOptions,
        errors,
        setErrors,
        formatPriceWithSpaces,
        clampPriceToMax,
        getBillingDescription,
        isSubmitting,
        isPreviewStep,
      }: CreateSubscriptionFormProps) {
        return (
          <>
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                {isPreviewStep ? 'Podgląd planu' : 'Szczegóły planu'}
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4 ">
                  <TextField
                    type="text"
                    inputMode="text"
                    label="Imię i nazwisko"
                    maxLength={200}
                    width={'50%'}
                    autoFocus={true}
                    value={formData.planName}
                    onChange={(e) => {
                      if (errors.planName) {
                        setErrors((prev) => ({
                          ...prev,
                          planName: '',
                        }));
                      }
                      const next = e.target.value.trimStart().slice(0, 200);
                      setFormData((prev) => ({
                        ...prev,
                        planName: next,
                      }));
                    }}
                    error={!!errors.planName}
                    errorMessage={errors.planName ?? ''}
                    disabled={isSubmitting}
                    readOnly={isPreviewStep}
                  />
                </div>
                <Textarea
                  error={!!errors.description}
                  readOnly={isPreviewStep}
                  errorMessage={errors.description ?? ''}
                  label="Opis"
                  maxLength={500}
                  fullWidth
                  rows={4}
                  value={formData.description}
                  onChange={(e) => {
                    if (errors.description) {
                      setErrors((prev) => ({
                        ...prev,
                        description: '',
                      }));
                    }
                    setFormData((prev) => ({
                      ...prev,
                      description:
                        e.target.value.trimStart().slice(0, 500) || '',
                    }));
                  }}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Okres i rozliczenia
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <Dropdown
                    menuFullWidth
                    items={PERIOD_TYPE_OPTIONS}
                    selectedId={durationType}
                    error={!!errors.durationType}
                    errorMessage={errors.durationType ?? ''}
                    trigger={({ open, toggle, disabled, readOnly, error }) => (
                      <DropDownButton
                        label="Typ okresu"
                        value={durationType}
                        open={open}
                        toggle={toggle}
                        disabled={disabled}
                        readOnly={readOnly ?? false}
                        error={error}
                        hideChevron={readOnly ?? false}
                      />
                    )}
                    onItemClick={(id) => {
                      if (errors.durationType) {
                        setErrors((prev) => ({
                          ...prev,
                          durationType: '',
                        }));
                      }
                      setDurationType(id);
                    }}
                    disabled={isSubmitting}
                    readOnly={isPreviewStep}
                  />
                  <Dropdown
                    key={durationType || 'empty'}
                    menuFullWidth
                    items={
                      durationType === 'Miesięczny'
                        ? PERIOD_OPTIONS_MONTHLY
                        : PERIOD_OPTIONS_YEARLY
                    }
                    selectedId={period}
                    error={!!errors.period}
                    errorMessage={errors.period ?? ''}
                    trigger={({ open, toggle, disabled, readOnly, error }) => (
                      <DropDownButton
                        label="Okres"
                        value={period}
                        open={open}
                        toggle={toggle}
                        disabled={disabled}
                        readOnly={readOnly ?? false}
                        error={error}
                        hideChevron={readOnly ?? false}
                      />
                    )}
                    onItemClick={(id) => {
                      if (errors.period) {
                        setErrors((prev) => ({
                          ...prev,
                          period: '',
                        }));
                      }
                      setPeriod(id);
                    }}
                    disabled={durationType.length === 0 || isSubmitting}
                    readOnly={isPreviewStep}
                  />
                  {cycleOptions.length === 1 ? (
                    <TextField
                      label="Cykl rozliczeniowy"
                      value={cycle}
                      fullWidth={true}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                    />
                  ) : (
                    <Dropdown
                      key={`${durationType}-${period}`}
                      menuFullWidth
                      items={cycleOptions}
                      selectedId={cycle}
                      error={!!errors.cycle}
                      errorMessage={errors.cycle ?? ''}
                      trigger={({
                        open,
                        toggle,
                        disabled,
                        readOnly,
                        error,
                      }) => (
                        <DropDownButton
                          label="Cykl rozliczeniowy"
                          value={cycle}
                          open={open}
                          toggle={toggle}
                          disabled={disabled}
                          readOnly={readOnly ?? false}
                          error={error}
                          hideChevron={readOnly ?? false}
                        />
                      )}
                      onItemClick={(id) => {
                        if (errors.cycle) {
                          setErrors((prev) => ({
                            ...prev,
                            cycle: '',
                          }));
                        }
                        setCycle(id);
                      }}
                      disabled={
                        durationType.length === 0 ||
                        period.length === 0 ||
                        isSubmitting
                      }
                      readOnly={isPreviewStep}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">Cennik</p>
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                  <div className="w-[32.7%]">
                    <TextField
                      fullWidth={true}
                      label="Cena"
                      maxLength={200}
                      type="amount"
                      value={formatPriceWithSpaces(formData.price)}
                      onChange={(e) => {
                        if (errors.price) {
                          setErrors((prev) => ({
                            ...prev,
                            price: '',
                          }));
                        }
                        const raw = e.target.value.replace(/\s/g, '');
                        const clamped = clampPriceToMax(raw);
                        setFormData((prev) => ({
                          ...prev,
                          price: clamped === raw ? raw : clamped,
                        }));
                      }}
                      error={!!errors.price}
                      errorMessage={errors.price ?? ''}
                      disabled={isSubmitting}
                      trailingIcon={
                        formData.price ? (
                          <span className="text-bodyLarge text-neutral-10 align-middle ml-[2px] mt-[12px]">
                            zł
                          </span>
                        ) : null
                      }
                      readOnly={isPreviewStep}
                    />
                  </div>
                  <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                    {getBillingDescription(
                      formData.price,
                      cycle,
                      period,
                      formatPriceWithSpaces
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Limity użytkowania
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <div className="flex flex-col gap-2 w-full">
                    <TextField
                      label="Liczba użytkowników"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={3}
                      fullWidth
                      value={formData.userCount}
                      onChange={(e) => {
                        if (errors.userCount) {
                          setErrors((prev) => ({
                            ...prev,
                            userCount: '',
                          }));
                        }
                        const digitsOnly = e.target.value.replace(/\D/g, '');

                        if (digitsOnly === '') {
                          setFormData((prev) => ({ ...prev, userCount: '' }));
                          return;
                        }

                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(500, Math.max(0, num));

                        setFormData((prev) => ({
                          ...prev,
                          userCount: String(clamped),
                        }));
                      }}
                      error={!!errors.userCount}
                      errorMessage={errors.userCount ?? ''}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                    />

                    {!errors.userCount && (
                      <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                        Maximum allowed: 500 users
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <TextField
                      label="Liczba placówek"
                      maxLength={2}
                      fullWidth
                      type="text"
                      inputMode="numeric"
                      value={formData.facilityCount}
                      onChange={(e) => {
                        if (errors.facilityCount) {
                          setErrors((prev) => ({
                            ...prev,
                            facilityCount: '',
                          }));
                        }
                        const digitsOnly = e.target.value.replace(/\D/g, '');

                        if (digitsOnly === '') {
                          setFormData((prev) => ({
                            ...prev,
                            facilityCount: '',
                          }));
                          return;
                        }

                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(10, Math.max(0, num));

                        setFormData((prev) => ({
                          ...prev,
                          facilityCount: String(clamped),
                        }));
                      }}
                      error={!!errors.facilityCount}
                      errorMessage={errors.facilityCount ?? ''}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                    />

                    {!errors.facilityCount && (
                      <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                        Maximum allowed: 10 facilities
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <TextField
                      label={
                        <>
                          Liczba dokumentów QMS{' '}
                          <span style={{ fontSize: 12 }}>(Optional)</span>
                        </>
                      }
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={4}
                      fullWidth={true}
                      value={formData.qmsDocCount}
                      onChange={(e) => {
                        const digitsOnly = e.target.value.replace(/\D/g, '');
                        if (digitsOnly === '') {
                          setFormData((prev) => ({
                            ...prev,
                            qmsDocCount: '',
                          }));
                          return;
                        }
                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(2000, Math.max(0, num));
                        setFormData((prev) => ({
                          ...prev,
                          qmsDocCount: String(clamped),
                        }));
                      }}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      },
    []
  );

  return (
    <CreateLayout
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      isCancelModalOpen={isCancelModalOpen}
      handleCloseCancelModal={handleCloseCancelModal}
      handleConfirmCancel={handleConfirmCancel}
      isSnackbarOpen={isSnackbarOpen}
      handleCloseSnackbar={handleCloseSnackbar}
      snackbarError={snackbarError}
      Header={CreateSubscriptionHeader}
      cancelButtonLabel={isPreviewStep ? 'Wróć do edycji' : 'Anuluj'}
      submitButtonLabel={isPreviewStep ? 'Utwórz plan' : 'Przejdź do podglądu'}
      submitButtonLoadingLabel="Tworzenie planu..."
    >
      <CreateSubscriptionForm
        formData={formData}
        setFormData={setFormData}
        durationType={durationType}
        setDurationType={setDurationType}
        period={period}
        setPeriod={setPeriod}
        cycle={cycle}
        setCycle={setCycle}
        cycleOptions={cycleOptions}
        errors={errors}
        setErrors={setErrors}
        formatPriceWithSpaces={formatPriceWithSpaces}
        clampPriceToMax={clampPriceToMax}
        maxPriceFormatted={formatPriceWithSpaces(String(MAX_PRICE))}
        getBillingDescription={getBillingDescription}
        isSubmitting={isSubmitting}
        isPreviewStep={isPreviewStep}
      />
    </CreateLayout>
  );
};

export default CreateSubscription;
